// src/Views/PortfolioGrid.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './PortfolioGrid.css';
import projects from '../data/projects';

const MOBILE_ORDER_MQ = '(max-width: 1024px)';

/** On mobile/tablet, pin TravelPerk to 2nd; desktop keeps curated projects.js order. */
const orderForViewport = (list, isNarrow) => {
  if (!isNarrow) return list;
  const travelperkIndex = list.findIndex((project) => project.to === '/travelperk');
  if (travelperkIndex <= 1) return list;
  const next = [...list];
  const [travelperk] = next.splice(travelperkIndex, 1);
  next.splice(1, 0, travelperk);
  return next;
};

/**
 * Desktop masonry columns: equal vertical gaps (throws off row alignment).
 * Left: Keymotion, Adare, Evo… — Right: TravelPerk, Healthcare… — Varietal bottom-right.
 */
const splitDesktopColumns = (list) => {
  const left = [];
  const right = [];
  const used = new Set();

  const take = (to, column) => {
    const project = list.find((item) => item.to === to);
    if (!project) return;
    column.push(project);
    used.add(to);
  };

  take('/keymotion', left);
  take('/adarecollection', left);
  take('/evoagency', left);

  take('/travelperk', right);
  take('/healthcare', right);

  const varietal = list.find((item) => item.to === '/varietal');
  if (varietal) used.add('/varietal');

  let toLeft = true;
  list.forEach((project) => {
    if (used.has(project.to)) return;
    if (toLeft) left.push(project);
    else right.push(project);
    toLeft = !toLeft;
  });

  if (varietal) right.push(varietal);

  return { left, right };
};

const itemClassName = (project, state) =>
  [
    'portfolio-item',
    `is-${state || 'instant'}`,
    project.shorter ? 'portfolio-item--shorter' : '',
    project.ratio === '16x9' ? 'portfolio-item--ratio-16x9' : '',
  ]
    .filter(Boolean)
    .join(' ');

// State per block: 'instant' -> visible, no animation (in view on load)
//                  'hidden'  -> below the fold, waiting to be scrolled into view
//                  'anim'    -> scrolled into view, plays the rise + fade
const PortfolioGrid = () => {
  const itemRefs = useRef([]);
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(MOBILE_ORDER_MQ).matches
      : false
  );
  const displayProjects = useMemo(
    () => orderForViewport(projects, isNarrow),
    [isNarrow]
  );
  const desktopColumns = useMemo(
    () => (isNarrow ? null : splitDesktopColumns(displayProjects)),
    [displayProjects, isNarrow]
  );
  // Default visible so there's no flash and nothing animates unexpectedly.
  const [states, setStates] = useState(() => displayProjects.map(() => 'instant'));

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_ORDER_MQ);
    const onChange = () => setIsNarrow(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    setStates(displayProjects.map(() => 'instant'));
    itemRefs.current = [];

    let cancelled = false;
    let ran = false;
    let observer;

    const classify = () => {
      if (cancelled || ran) return;
      ran = true;

      const vh = window.innerHeight;

      // Images are loaded now, so block positions are final. Hide only the
      // blocks that start below the fold so they can animate in on scroll.
      setStates((prev) =>
        prev.map((state, i) => {
          const el = itemRefs.current[i];
          if (!el) return state;
          return el.getBoundingClientRect().top >= vh ? 'hidden' : 'instant';
        })
      );

      if (typeof IntersectionObserver === 'undefined') return;

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const index = Number(entry.target.dataset.index);
            setStates((prev) => {
              if (prev[index] !== 'hidden') return prev;
              const next = [...prev];
              next[index] = 'anim';
              return next;
            });
            observer.unobserve(entry.target);
          });
        },
        // Fire as the block enters the bottom of the viewport (matches
        // lucastruchen.com), independent of the block's height.
        { threshold: 0, rootMargin: '0px 0px -12% 0px' }
      );
      itemRefs.current.forEach((el) => el && observer.observe(el));
    };

    // Wait for the thumbnail images to load so positions are final before we
    // decide which blocks are below the fold. (In a SPA the <img> tags mount
    // after window.load, so we track each image individually.)
    const imgs = itemRefs.current
      .map((el) => el && el.querySelector('img, video'))
      .filter(Boolean);

    let remaining = imgs.length;
    if (remaining === 0) {
      classify();
    } else {
      const onOne = () => {
        remaining -= 1;
        if (remaining <= 0) classify();
      };
      imgs.forEach((img) => {
        if (img.tagName === 'VIDEO') {
          if (img.readyState >= 1) {
            onOne();
          } else {
            img.addEventListener('loadeddata', onOne, { once: true });
            img.addEventListener('error', onOne, { once: true });
          }
          return;
        }
        if (img.complete) {
          onOne();
        } else {
          img.addEventListener('load', onOne, { once: true });
          img.addEventListener('error', onOne, { once: true });
        }
      });
    }

    // Safety net in case some image events never fire.
    const fallback = setTimeout(classify, 2000);

    return () => {
      cancelled = true;
      clearTimeout(fallback);
      if (observer) observer.disconnect();
    };
  }, [displayProjects, desktopColumns]);

  const indexByTo = useMemo(
    () =>
      Object.fromEntries(displayProjects.map((project, index) => [project.to, index])),
    [displayProjects]
  );

  const renderItem = (project) => {
    const index = indexByTo[project.to];
    return (
      <Link
        key={project.to}
        to={project.to}
        data-index={index}
        ref={(el) => {
          itemRefs.current[index] = el;
        }}
        className={itemClassName(project, states[index])}
      >
        <div className="portfolio-item__media">
          {project.video ? (
            <video
              className="portfolio-item__image"
              src={project.video}
              poster={project.img}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-label={project.title}
            />
          ) : (
            <img
              src={project.img}
              alt={project.title}
              className="portfolio-item__image"
            />
          )}
        </div>
      </Link>
    );
  };

  if (desktopColumns) {
    return (
      <div className="portfolio-grid portfolio-grid--masonry">
        <div className="portfolio-grid__col">
          {desktopColumns.left.map(renderItem)}
        </div>
        <div className="portfolio-grid__col">
          {desktopColumns.right.map(renderItem)}
        </div>
      </div>
    );
  }

  return (
    <div className="portfolio-grid portfolio-grid--stack">
      {displayProjects.map(renderItem)}
    </div>
  );
};

export default PortfolioGrid;
